import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { BaseComponent } from '@ex/module/core/domain/base-component';
import { DATA_TOKEN, ICDKPortalComponent } from '../../Theme/services/zen-cdk.service';
import {
  ExBenchmarkService,
  BenchmarkOptionsRequestDto,
  BenchmarkOptionsResponseDto,
  BenchmarkRequestDto,
  BenchmarkResponseDto,
  BenchmarkFactorValue,
  HappinessBenchmarkValues,
  EngagementBenchmarkValues
} from '@ex/module/core/services/ex-benchmark.service';
import { ChildrenAnimation } from '../../animations/allAnimations';
import { take, map } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';
import { EngagementAnalysis } from '@ex/module/core/data-models/engagement-analysis';
import { EngagementPreviousData } from '@ex/module/core/data-models/engagement-previous-data';
import { ProductBenchmarkService } from './data-access/services/product-benchmark.service';
import { KeyResolverService } from '@ex/module/core/services/key-resolver.service';
import FactorData from './data-access/models/factor-data';


export interface ProductBenchmarkRequest extends BenchmarkOptionsRequestDto {
  showPrevious?: boolean;
  showMENA?: boolean;
  showTopPerformers?: boolean;
  showCountry?: boolean;
  showIndustry?: boolean;
  industryText?: string,
  regionOrCountryText?: string,
}
export interface benchmarkData {
  benchmarkType: string;
  value: number;
  topPerformer?: number;
  icon: string;
  benchmarkDiff: number;
  topPerformerDiff?: number;
}

type BenchmarkValues = HappinessBenchmarkValues | EngagementBenchmarkValues | BenchmarkFactorValue;
export type BenchmarkPayload = {
  [key: string]: BenchmarkResponseDto<BenchmarkValues>[];
} | {
  previous?: EngagementPreviousData;
};


@Component({
  selector: 'app-product-benchmark',
  templateUrl: './product-benchmark.component.html',
  styleUrls: ['./product-benchmark.component.scss'],
  animations: [ChildrenAnimation],
  providers: [ProductBenchmarkService]
})
export class ProductBenchmarkComponent extends BaseComponent implements OnInit, ICDKPortalComponent {
  readonly year = new Date().getFullYear();

  showPrevious = false;
  open = true;

  availableIndustry = false;
  availableCountry = false;
  benchmarkOptions: ProductBenchmarkRequest;

  @Output() close = new EventEmitter<void>();
  @Output() results = new EventEmitter<{ payload?: BenchmarkPayload, request: ProductBenchmarkRequest, response: FactorData[] }>();
  language: string;

  constructor(
    @Inject(DATA_TOKEN)
    public data: { language: string; previousData: EngagementPreviousData; request: ProductBenchmarkRequest; factors: FactorData[] },
    private benchmarkService: ExBenchmarkService, private productBenchmarkService: ProductBenchmarkService,
    private keyResolverService: KeyResolverService
  ) {
    super();
  }

  ngOnInit(): void {
    this.language = this.data.language; 
    this.benchmarkOptions = { ...this.data.request }
    const { industry, regionOrCountry, productType } = this.benchmarkOptions;
    this.benchmarkService.getBenchmarkOptions({ industry, regionOrCountry, productType, year: this.year })
      .pipe(take(1),).
      subscribe((resp: BenchmarkOptionsResponseDto) => {
        this.availableIndustry = resp.availableOptions.some(x => x.industry === industry);
        this.availableCountry = resp.availableOptions.some(x => x.regionOrCountry === regionOrCountry);
      })
  }

  closeModal(): void {
    this.open = false;
    this.close.emit();
  }

  fetchBenchmark(): void {
    const { industry, regionOrCountry, showMENA, showCountry, showIndustry, showPrevious } = this.benchmarkOptions;

    const requests: Observable<{ key: string; data: BenchmarkResponseDto<BenchmarkValues>[] }>[] = [];
    const payload: BenchmarkPayload = {};

    if (showMENA) requests.push(this.createBenchmarkRequest('ALL', 'MENA', 'mena'));
    if (showCountry) requests.push(this.createBenchmarkRequest('ALL', regionOrCountry, 'country'));
    if (showIndustry) requests.push(this.createBenchmarkRequest(industry, 'MENA', 'industry'));

    // If no benchmarks selected but showPrevious is true
    if (!showIndustry && !showCountry && !showMENA && showPrevious) {
      payload['previous'] = this.data.previousData;
      const factors = this.productBenchmarkService.calculateBenchmark(this.data?.factors, this.data?.previousData, payload)

      this.productBenchmarkService.setBenchmarkData({ payload, request: this.benchmarkOptions, response: factors });
      this.results.emit({ payload, request: this.benchmarkOptions, response: factors });
      return;
    }

    // If nothing is selected
    if (!showIndustry && !showCountry && !showMENA && !showPrevious) {
      this.productBenchmarkService.setBenchmarkData({ payload, request: this.benchmarkOptions, response: [] });
      this.results.emit({ payload: null, request: this.benchmarkOptions, response: [] });
      return;
    }

    forkJoin(requests).pipe(take(1)).subscribe(resultsArray => {
      if (showPrevious && this.data.previousData) {
        payload['previous'] = this.data.previousData;
      }
      Object.assign(payload, ...resultsArray.map(r => ({ [r.key]: r.data })));
      const factors = this.productBenchmarkService.calculateBenchmark(this.data?.factors, this.data?.previousData, payload)

      this.productBenchmarkService.setBenchmarkData({ payload, request: this.benchmarkOptions, response: factors });
      this.results.emit({ payload, request: this.benchmarkOptions, response: factors });
    });
  }

  private createBenchmarkRequest(
    industry: string,
    regionCountry: string,
    key: string
  ): Observable<{ key: string; data: BenchmarkResponseDto<BenchmarkValues>[] }> {

    const req: BenchmarkRequestDto = {
      industry,
      regionCountry,
      year: this.year,
    };
    let call$: Observable<BenchmarkResponseDto<BenchmarkValues>[]>;
    switch (this.benchmarkOptions.productType) {
      case 'ENG':
        call$ = this.benchmarkService.getEngagementBenchmark(req) as Observable<BenchmarkResponseDto<BenchmarkValues>[]>;
        break;
      case 'HAP':
        call$ = this.benchmarkService.getHappinessBenchmark(req) as Observable<BenchmarkResponseDto<BenchmarkValues>[]>;
        break;
      case 'AIR':
        call$ = this.benchmarkService.getAiReadinessBenchmark(req) as Observable<BenchmarkResponseDto<BenchmarkValues>[]>;
        break;
    }
    return call$.pipe(
      map(data => {
        data = data.find(
          x => x.regionCountryId === regionCountry && x.industryId === industry
        ) as any;
        return { key, data };
      })
    );
  }
  toggleTopPerformer() {
    if (!this.benchmarkOptions.showMENA) {
      this.benchmarkOptions.showTopPerformers = false
    }
  }

}
